import { useRecoilState } from 'recoil';
import { useEffect, useRef } from 'react';
import { asrPlexVADWebSocketService } from './ASRPlexWebSocketService';
import { logger } from 'modules/logger';
import { asrPlexVadWebSocketState } from './state';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';

/**
 * Maintains a persistent WebSocket to the ASRPlex VAD service with automatic retries.
 *
 * - Connects on mount, stores socket in Recoil.
 * - Resets retry count on `open`.
 * - On `close`, retries with exponential backoff (max 30s) + jitter.
 * - Cleans up listeners on unmount.
 *
 * @returns Current WebSocket, or `null`/`undefined` if not connected.
 */
export const useRetryASRPlexVADWebSocket = (): Nullable<WebSocket> => {
  const [socketState, setSocketState] = useRecoilState(asrPlexVadWebSocketState);
  const attemptsRef = useRef(0);
  const [asrPlexFeatureEnabled] = useFeatureFlagEnabled(FF.REPORTER_ASR_PLEX);

  useEffect(() => {
    if (asrPlexFeatureEnabled) {
      return;
    }

    let socket = asrPlexVADWebSocketService.connect();

    const onOpen = () => {
      // Reset the attempts counter
      attemptsRef.current = 1;
      setSocketState((prev) => ({
        ...prev,
        socket,
      }));
    };
    const onClose = () => {
      // Cleanup previous event listeners
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('close', onClose);

      // Exponential backoff, capping at 30 seconds after 5 attempts
      // 2 ** 1 = 2, 2 ** 2 = 4, 2 ** 3 = 8, 2 ** 4 = 16, 2 ** 5 = 32
      const backoff = Math.min(1000 * 2 ** attemptsRef.current, 30000);
      const jitter = Math.floor(Math.random() * 50);

      logger.info(
        `[ASRPlexVADDictation] WebSocket connection closed, retrying connection in ${
          backoff + jitter
        } ms...`,
        {
          attempts: attemptsRef.current,
          backoff,
          jitter,
        }
      );
      setTimeout(() => {
        socket = asrPlexVADWebSocketService.connect();
        attemptsRef.current += 1;
        socket.addEventListener('open', onOpen);
        socket.addEventListener('close', onClose);
      }, backoff + jitter);
    };
    socket.addEventListener('open', onOpen);
    socket.addEventListener('close', onClose);

    return () => {
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('close', onClose);
    };
  }, [asrPlexFeatureEnabled, setSocketState]);

  return socketState.socket;
};
